import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEOHead = ({ title, description, canonical, noIndex = false }) => {
  const location = useLocation();

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && description) {
      metaDescription.setAttribute('content', description);
    }

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    
    const canonicalUrl = canonical || `https://www.wennze.com${location.pathname}`;
    canonicalLink.setAttribute('href', canonicalUrl);

    // Handle robots meta tag
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    
    if (noIndex) {
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else {
      robotsMeta.setAttribute('content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    }

    // Update Open Graph tags
    const updateOGTag = (property, content) => {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', content);
    };

    if (title) updateOGTag('og:title', title);
    if (description) updateOGTag('og:description', description);
    updateOGTag('og:url', canonicalUrl);

    // Update Twitter tags
    const updateTwitterTag = (name, content) => {
      let twitterTag = document.querySelector(`meta[name="${name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.setAttribute('name', name);
        document.head.appendChild(twitterTag);
      }
      twitterTag.setAttribute('content', content);
    };

    if (title) updateTwitterTag('twitter:title', title);
    if (description) updateTwitterTag('twitter:description', description);
    updateTwitterTag('twitter:url', canonicalUrl);

  }, [title, description, canonical, noIndex, location.pathname]);

  return null; // This component doesn't render anything
};

// Page-specific SEO configurations
export const getPageSEO = (pathname, params = {}) => {
  const baseUrl = 'https://www.wennze.com';
  
  const seoConfig = {
    '/': {
      title: 'Wennze - La Marketplace N°1 en RD Congo | Achetez et Vendez en Ligne',
      description: 'Wennze - La première marketplace en RD Congo pour acheter et vendre. Découvrez des produits locaux, importés et des services à Kinshasa, Lubumbashi, Goma et partout en RD Congo',
      canonical: baseUrl + '/'
    },
    '/dashboard': {
      title: 'Tableau de Bord - Wennze',
      description: 'Accédez à votre tableau de bord Wennze pour gérer vos annonces, voir vos messages et découvrir les derniers produits en RD Congo',
      canonical: baseUrl + '/dashboard'
    },
    '/products/all': {
      title: 'Tous les Produits - Marketplace Wennze RD Congo',
      description: 'Découvrez tous les produits disponibles sur Wennze - électronique, mode, immobilier, voitures et plus à Kinshasa, Lubumbashi, Goma',
      canonical: baseUrl + '/products/all'
    },
    '/about': {
      title: 'À Propos de Wennze - Marketplace Leader en RD Congo',
      description: 'Découvrez Wennze, la marketplace numéro 1 en République Démocratique du Congo. Notre mission, notre équipe et notre impact sur le commerce local',
      canonical: baseUrl + '/about'
    },
    '/contact': {
      title: 'Contactez-Nous - Support Wennze RD Congo',
      description: 'Contactez l\'équipe Wennze pour toute question, support technique ou partenariat. Service client disponible pour tous les utilisateurs en RD Congo',
      canonical: baseUrl + '/contact'
    },
    '/register': {
      title: 'Inscription - Rejoignez Wennze Marketplace RD Congo',
      description: 'Créez votre compte Wennze gratuitement et commencez à acheter et vendre en ligne en RD Congo. Inscription rapide et sécurisée',
      canonical: baseUrl + '/register'
    },
    '/login': {
      title: 'Connexion - Accédez à votre Compte Wennze',
      description: 'Connectez-vous à votre compte Wennze pour accéder à vos annonces, messages et profil sur la marketplace N°1 en RD Congo',
      canonical: baseUrl + '/login',
      noIndex: true // Login pages shouldn't be indexed
    },
    '/users/discover': {
      title: 'Découvrir des Utilisateurs - Communauté Wennze',
      description: 'Découvrez et connectez-vous avec d\'autres utilisateurs Wennze en RD Congo. Suivez vos vendeurs préférés et agrandissez votre réseau',
      canonical: baseUrl + '/users/discover'
    },
    '/search': {
      title: 'Recherche de Produits - Wennze Marketplace RD Congo',
      description: 'Recherchez parmi des milliers de produits et services sur Wennze. Trouvez ce que vous cherchez à Kinshasa, Lubumbashi, Goma et partout en RD Congo',
      canonical: baseUrl + '/search'
    },
    '/forgot-password': {
      title: 'Mot de Passe Oublié - Wennze',
      description: 'Réinitialisez votre mot de passe Wennze en toute sécurité',
      canonical: baseUrl + '/forgot-password',
      noIndex: true
    },
    '/server-down': {
      title: 'Maintenance - Wennze',
      description: 'Wennze est temporairement en maintenance. Nous reviendrons bientôt.',
      canonical: baseUrl + '/server-down',
      noIndex: true
    }
  };

  // Handle dynamic routes
  if (pathname.startsWith('/products/view/') && params.id) {
    return {
      title: `Produit ${params.id} - Wennze Marketplace RD Congo`,
      description: `Découvrez ce produit sur Wennze, la marketplace leader en RD Congo. Achat sécurisé avec livraison dans toute la RD Congo`,
      canonical: baseUrl + pathname
    };
  }

  if (pathname.startsWith('/products/category/') && params.categoryName) {
    return {
      title: `Catégorie ${params.categoryName} - Wennze Marketplace RD Congo`,
      description: `Parcourez les produits de la catégorie ${params.categoryName} sur Wennze. Large sélection disponible partout en RD Congo`,
      canonical: baseUrl + pathname
    };
  }

  if (pathname.startsWith('/user/corner/') && params.userId) {
    return {
      title: `Boutique Utilisateur - Wennze Marketplace RD Congo`,
      description: `Découvrez la boutique de cet utilisateur sur Wennze. Produits de qualité vendus par des particuliers et professionnels en RD Congo`,
      canonical: baseUrl + pathname
    };
  }

  // Handle protected/private routes that shouldn't be indexed
  if (pathname.startsWith('/messages/') || 
      pathname.startsWith('/profile') || 
      pathname.startsWith('/user/notifications') ||
      pathname.startsWith('/portefeuille') ||
      pathname.startsWith('/add-post') ||
      pathname.startsWith('/edit-post/')) {
    return {
      title: 'Espace Personnel - Wennze',
      description: 'Espace personnel Wennze',
      canonical: baseUrl + pathname,
      noIndex: true
    };
  }

  return seoConfig[pathname] || {
    title: 'Wennze - Marketplace N°1 en RD Congo',
    description: 'Découvrez Wennze, la marketplace leader en République Démocratique du Congo',
    canonical: baseUrl + pathname
  };
};

export default SEOHead;
